import { AnimatedSection } from "@/components/shared/AnimatedSection";
import { SectionLabel } from "@/components/shared/SectionLabel";
import { BgOrbs } from "@/components/shared/BgOrbs";
import { SparkStar } from "@/components/shared/SVGShapes";
import {
  ProjectCard,
  type ProjectCardData,
} from "@/components/shared/ProjectCard";
import { asset } from "@/lib/assets";
const approvalImg = asset("case-studies/ClientApproval/Approval.png");
const letteringImg = asset("case-studies/lettering/groove.png");
const uiExamplesImg = asset("case-studies/UIexamples/UI-example.png");
const notificationImg = asset(
  "case-studies/notification/notificationcenter2.png",
);
const liveActivitiesImg = asset("case-studies/liveActivities/LiveActivityThumbnail.png");
const designSystemImg = asset("case-studies/inconsistencies/Designsystemthumbnail.png");

const CASE_STUDIES: ProjectCardData[] = [
  {
    id: "loan-signing",
    tag: "Mobile flow",
    category: "UI | UX",
    title: "Improving loan pre-approval experience",
    description: "Making loan terms easy to review, understand, and sign.",
    color: "#E8603E",
    image: approvalImg,
    href: "/case-study/loan-signing",
  },
  {
    id: "live-activities",
    tag: "App",
    category: "UI",
    title: "Live Activity for iOS",
    description: "Increasing app adoption with timely notifications",
    color: "#E8603E",
    image: liveActivitiesImg,
    href: "/case-study/live-activities",
  },
  {
    id: "ui-examples",
    tag: "UI work",
    category: "Mobile · Web · Style guides · Visual Design",
    title: "UI examples",
    description:
      "A selection of interfaces I've designed across products, platforms, and years.",
    color: "#E8603E",
    image: uiExamplesImg,
    href: "/case-study/ui-examples",
  },
  // hidden for now — may re-enable later
  // {
  //   id: "d2c-quiz",
  //   tag: "Conversion",
  //   category: "UX | Research",
  //   title: "Increasing a D2C quiz conversion rate in weeks not months",
  //   description:
  //     "Rapid experimentation on a high-traffic lead-gen experience with AI-assisted prototyping.",
  //   color: "#2B2A52",
  //   href: "/case-study/quiz",
  // },
  {
    id: "notification-wallet",
    tag: "App",
    category: "UX | UI library | User testing",
    title: "Notification center for a digital wallet",
    description:
      "Giving visibility of transactions, payment reminders, and promotions with informational and actionable updates.",
    color: "#E8603E",
    image: notificationImg,
    href: "/case-study/notification-wallet",
  },
  // hidden for now — restore exactly as-is when needed
  // {
  //   id: "homelight-agent",
  //   tag: "AI Product",
  //   category: "UX | AI",
  //   title: "Shipping Products in the AI Era",
  //   description:
  //     "Taking small initiatives from idea to production and shipping them solo.",
  //   color: "#4A3F8F",
  //   href: "/case-study/homelight-offers",
  // },
  {
    id: "design-system",
    tag: "Design System",
    category: "Design Ops",
    title: "Migrating our design system to Cursor with Figma MCP",
    description:
      "Figma MCP + Code Connect eliminated the translation gap between tokens and code.",
    color: "#E8603E",
    image: designSystemImg,
    href: "/case-study/design-system",
  },
  {
    id: "lettering",
    tag: "Personal projects",
    tagColor: "#ffade3",
    category: "Paper | Procreate | Illustrator",
    title: "Hand lettering",
    description: "Lettering compositions made by me from scratch",
    color: "#3535E8",
    image: letteringImg,
    href: "/case-study/lettering",
  },
];

/**
 * Case study project cards — floating folder shape, 2-col responsive grid.
 * Add new case studies to CASE_STUDIES array; the grid handles layout automatically.
 */
export function CaseStudies() {
  return (
    <section
      id="work"
      className="py-24 px-6 bg-[#F0F0F6] relative overflow-hidden"
    >
      <BgOrbs variant="yellow" />
      <SparkStar
        size={28}
        className="spark-float absolute"
        style={{ top: "5%", right: "8%", animationDelay: "0.4s" }}
      />
      <SparkStar
        size={16}
        className="spark-float absolute"
        style={{ bottom: "12%", left: "5%", animationDelay: "1.8s" }}
      />

      <div className="relative max-w-6xl mx-auto">
        <AnimatedSection>
          <SectionLabel>Work</SectionLabel>
          <h2 className="font-[Bricolage_Grotesque] font-medium tracking-[-0.04em] text-[clamp(1.75rem,4vw,3rem)] text-[#2B2A52] mb-12 flex items-center gap-3">
            Selected work
            <img
              src="/stickers/spark-1.svg"
              aria-hidden="true"
              className="spark-float w-8 h-8 inline-block"
              style={{ animationDelay: "0.2s" }}
            />
          </h2>

        </AnimatedSection>

        {/* Responsive grid: 1 col mobile → 2 col desktop.
            Odd last card is centered at single-column width. */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
          {CASE_STUDIES.map((cs, i) => {
            const isLastOdd =
              CASE_STUDIES.length % 2 !== 0 && i === CASE_STUDIES.length - 1;
            return (
              <AnimatedSection
                key={cs.id}
                delay={i * 80}
                className={isLastOdd ? "md:col-span-2" : ""}
              >
                <div
                  className={
                    isLastOdd ? "md:w-[calc(50%-1rem)] md:mx-auto" : ""
                  }
                >
                  <ProjectCard {...cs} delay={i * 80} />
                </div>
              </AnimatedSection>
            );
          })}
        </div>
      </div>
    </section>
  );
}
